/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect } from 'react';
import { useAdmin } from '@/contexts/AdminContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Zap, Timer, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyPromo = {
  type: 'exit_popup',
  title: '',
  description: '',
  discount_text: '',
  cta_text: 'Get Offer',
  cta_link: '/get-quote',
  image_url: '',
  is_active: true,
  end_date: '',
  bg_color: '#dc2626',
  text_color: '#ffffff',
};

export default function AdminPromotions() {
  const { token } = useAdmin();
  const [promos, setPromos] = useState([]);
  const [form, setForm] = useState(emptyPromo);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    const { data } = await axios.get(`${API}/admin/promotions`, { headers });
    setPromos(data);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title) return toast.error('Title is required');
    try {
      if (editing) {
        await axios.put(`${API}/admin/promotions/${editing}`, form, { headers });
        toast.success('Promotion updated');
      } else {
        await axios.post(`${API}/admin/promotions`, form, { headers });
        toast.success('Promotion created');
      }
      setShowForm(false);
      setEditing(null);
      setForm(emptyPromo);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this promotion?')) return;
    await axios.delete(`${API}/admin/promotions/${id}`, { headers });
    toast.success('Deleted');
    load();
  };

  const edit = (p) => {
    setForm({ ...p });
    setEditing(p.id);
    setShowForm(true);
  };

  const toggle = async (p) => {
    await axios.put(`${API}/admin/promotions/${p.id}`, { is_active: !p.is_active }, { headers });
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-promotions">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Promotions & Offers</h1>
          <p className="text-slate-500 text-sm mt-1">Manage exit-intent popups and countdown banners</p>
        </div>
        <Button onClick={() => { setForm(emptyPromo); setEditing(null); setShowForm(true); }} data-testid="add-promotion-btn">
          <Plus className="w-4 h-4 mr-2" /> New Promotion
        </Button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{editing ? 'Edit' : 'Create'} Promotion</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowForm(false)}><X className="w-4 h-4" /></Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Type</Label>
                <select
                  className="w-full border rounded-md px-3 py-2 text-sm mt-1"
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  data-testid="promo-type-select"
                >
                  <option value="exit_popup">Exit-Intent Popup</option>
                  <option value="countdown_banner">Countdown Banner</option>
                </select>
              </div>
              <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="promo-title" /></div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="promo-description" /></div>
              <div><Label>Discount Text (e.g., "20% OFF")</Label><Input value={form.discount_text} onChange={(e) => setForm({ ...form, discount_text: e.target.value })} data-testid="promo-discount" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>CTA Text</Label><Input value={form.cta_text} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} /></div>
                <div><Label>CTA Link</Label><Input value={form.cta_link} onChange={(e) => setForm({ ...form, cta_link: e.target.value })} /></div>
              </div>
              <div><Label>Image URL (optional)</Label><Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} /></div>
              {form.type === 'countdown_banner' && (
                <div><Label>Countdown End Date</Label><Input type="datetime-local" value={form.end_date?.slice(0, 16) || ''} onChange={(e) => setForm({ ...form, end_date: new Date(e.target.value).toISOString() })} data-testid="promo-end-date" /></div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Background Color</Label><Input type="color" value={form.bg_color} onChange={(e) => setForm({ ...form, bg_color: e.target.value })} /></div>
                <div><Label>Text Color</Label><Input type="color" value={form.text_color} onChange={(e) => setForm({ ...form, text_color: e.target.value })} /></div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <Label>Active</Label>
              </div>
              <Button className="w-full" onClick={save} data-testid="save-promotion-btn">{editing ? 'Update' : 'Create'} Promotion</Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* List */}
      <div className="grid gap-4">
        {promos.length === 0 && <p className="text-slate-500 text-center py-8">No promotions yet. Create your first one!</p>}
        {promos.map((p) => (
          <Card key={p.id} className={`${!p.is_active ? 'opacity-60' : ''}`} data-testid={`promo-card-${p.id}`}>
            <CardContent className="py-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: p.bg_color, color: p.text_color }}>
                  {p.type === 'exit_popup' ? <Zap className="w-5 h-5" /> : <Timer className="w-5 h-5" />}
                </div>
                <div>
                  <p className="font-semibold text-slate-800">{p.title}</p>
                  <p className="text-xs text-slate-500">
                    {p.type === 'exit_popup' ? 'Exit Popup' : 'Countdown Banner'}
                    {p.discount_text && ` — ${p.discount_text}`}
                    {p.end_date && ` — Ends: ${new Date(p.end_date).toLocaleDateString()}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={p.is_active} onCheckedChange={() => toggle(p)} />
                <Button variant="ghost" size="icon" onClick={() => edit(p)} data-testid={`edit-promo-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => remove(p.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
